import { ColumnDef, Row } from "@tanstack/react-table";
import { type ComponentType, ReactNode } from "react";
import CopyButton from "@/components/CopyButton";
import { INTERNAL_ID_COPY_FIELD_LABELS } from "@/lib/copyAriaLabel";
import { SortFieldType } from "./sortUtils";

export type TableCopyButtonProps = {
  textToCopy: string;
  copyFieldLabel: string;
  copyFieldId?: string;
};

interface BuildCopyTextColumnProps<T> {
  accessorKey: string;
  header: string;
  getText: (row: T) => string | ReactNode;
  getSecondaryText?: (row: T) => string | ReactNode;
  /** Optional prefix (e.g. validation error icon) shown before the text. Does not affect copy value. */
  getPrefix?: (row: T) => ReactNode;
  shouldShowCopyButton?: (row: T) => boolean;
  /** Analytics-safe field name for the copy control (e.g. `PGA ID`, `Phone`). Do not embed PII. */
  copyFieldLabel: string;
  /** Optional internal ID for `data-amp-track-copy-field-id` on allowlisted fields (debug only; not in `aria-label`). */
  getCopyFieldId?: (row: T) => string | undefined;
  /** Optional copy control override (e.g. list-table native icon for Amplitude attribution). */
  CopyButtonComponent?: ComponentType<TableCopyButtonProps>;
  size?: number;
  minSize?: number;
  sortType?: SortFieldType;
  textClassName?: string;
  secondaryTextClassName?: string;
  className?: string;
  enableSorting?: boolean;
  meta?: Record<string, any>;
}

function resolveCopyFieldId(copyFieldLabel: string, textToCopy: string, explicitId?: string): string | undefined {
  if (explicitId) return explicitId;
  if (INTERNAL_ID_COPY_FIELD_LABELS.has(copyFieldLabel) && textToCopy) {
    return textToCopy;
  }
  return undefined;
}

/**
 * Helper to create a column that displays text with an optional copy button.
 *
 * The copy button is shown by default. Use `shouldShowCopyButton` to conditionally hide it.
 * When text is null/undefined/empty, displays "--" with opacity.
 *
 */
export function buildCopyTextColumn<T>({
  accessorKey,
  header,
  getText,
  getSecondaryText,
  getPrefix,
  shouldShowCopyButton,
  copyFieldLabel,
  getCopyFieldId,
  CopyButtonComponent,
  size,
  minSize,
  sortType,
  textClassName = "",
  secondaryTextClassName = "text-body-small text-sys-brand-on-surface-variant",
  className = "",
  enableSorting,
  meta
}: BuildCopyTextColumnProps<T>): ColumnDef<T> {
  const ResolvedCopyButton = CopyButtonComponent ?? CopyButton;

  const CellComponent = ({ row }: { row: Row<T> }) => {
    const text = getText(row.original);
    const secondaryText = getSecondaryText?.(row.original);
    const prefix = getPrefix?.(row.original);
    const showCopyButton = shouldShowCopyButton?.(row.original) ?? true;
    const textToCopy = typeof text === "string" ? text : String(text || "");
    const copyFieldId = resolveCopyFieldId(copyFieldLabel, textToCopy, getCopyFieldId?.(row.original));

    const displayText =
      text === null || text === undefined || text === "" ? <span className="opacity-20">--</span> : text;
    const hasSecondaryText = secondaryText !== null && secondaryText !== undefined && secondaryText !== "";

    if (!hasSecondaryText) {
      return (
        <div className={`flex items-center gap-2 ${className}`}>
          {prefix}
          <span className={textClassName}>{displayText}</span>
          {showCopyButton && textToCopy && (
            <ResolvedCopyButton textToCopy={textToCopy} copyFieldLabel={copyFieldLabel} copyFieldId={copyFieldId} />
          )}
        </div>
      );
    }

    return (
      <div className={`flex min-w-0 flex-col ${className}`}>
        <div className="flex items-center gap-2">
          {prefix}
          <span className={textClassName}>{displayText}</span>
          {showCopyButton && textToCopy && (
            <ResolvedCopyButton textToCopy={textToCopy} copyFieldLabel={copyFieldLabel} copyFieldId={copyFieldId} />
          )}
        </div>
        <div className={secondaryTextClassName}>{secondaryText}</div>
      </div>
    );
  };

  return {
    accessorKey,
    header,
    ...(size && { size }),
    ...(minSize && { minSize: minSize ?? size }),
    ...(enableSorting !== undefined && { enableSorting }),
    meta: {
      ...(sortType && { sortType }),
      ...meta
    },
    cell: CellComponent
  };
}
